/**
 * Moving the preview's clock.
 *
 * The resolver takes the instant as an argument, so asking "what is on this
 * screen at 11:00 on a Tuesday" is a real question with a real answer, not a
 * mock-up — the same function the TV runs is handed a different number. That is
 * the whole reason this control is worth building: a lunch daypart can be checked
 * on a Thursday afternoon in the office.
 *
 * The date and time are read in the STORE's timezone, because that is the only
 * clock any schedule decision is made against. A manager in Rotorua and a manager
 * abroad typing the same numbers get the same answer.
 *
 * The two fields are an anchor, not a live readout. They are set when somebody
 * jumps somewhere and then left alone; the clock runs on from that point, and the
 * panel shows where it has got to. Re-deriving the fields from the running clock
 * would make the minute field tick over underneath whoever was typing in it.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { offsetTo, parseScrubFields, scrubFields } from '../../lib/manifestPreview.js';

const HOUR_MS = 60 * 60 * 1000;

export interface ClockScrubberProps {
  /** The store's IANA zone; the fields are read and written in it. */
  timezone: string;
  offsetMs: number;
  onChange: (offsetMs: number) => void;
}

export function ClockScrubber({ timezone, offsetMs, onChange }: ClockScrubberProps): ReactElement {
  const [fields, setFields] = useState(() => scrubFields(Date.now() + offsetMs, timezone));

  const offsetRef = useRef(offsetMs);
  offsetRef.current = offsetMs;

  // A different screen can mean a different store and so a different zone. The
  // instant being previewed stays where it was; only the numbers describing it
  // change, which is the correct reading of "the same moment, over there".
  useEffect(() => {
    setFields(scrubFields(Date.now() + offsetRef.current, timezone));
  }, [timezone]);

  const applyInstant = (instantMs: number): void => {
    setFields(scrubFields(instantMs, timezone));
    onChange(offsetTo(instantMs, Date.now()));
  };

  const commit = (next: { date: string; time: string }): void => {
    setFields(next);
    const instant = parseScrubFields(next.date, next.time, timezone);
    // A half-typed date parses to nothing; hold the previous instant rather than
    // jumping the preview to whatever a partial field happens to mean.
    if (instant !== null) onChange(offsetTo(instant, Date.now()));
  };

  const nudge = (deltaMs: number): void => {
    const anchor = parseScrubFields(fields.date, fields.time, timezone) ?? Date.now() + offsetMs;
    applyInstant(anchor + deltaMs);
  };

  return (
    <div className="scrubber">
      <label className="field field--inline">
        <span className="field__label">Jump to day</span>
        <input
          type="date"
          className="input"
          value={fields.date}
          onChange={(event) => commit({ date: event.currentTarget.value, time: fields.time })}
        />
      </label>

      <label className="field field--inline">
        <span className="field__label">Time in store</span>
        <input
          type="time"
          className="input input--time"
          value={fields.time}
          onChange={(event) => commit({ date: fields.date, time: event.currentTarget.value })}
        />
      </label>

      <div className="scrubber__buttons">
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => nudge(-HOUR_MS)}
        >
          &minus;1 hour
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => nudge(HOUR_MS)}
        >
          +1 hour
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => applyInstant(Date.now())}
          disabled={offsetMs === 0}
        >
          Back to now
        </button>
      </div>
    </div>
  );
}
